import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
    EmailAuthProvider,
    GoogleAuthProvider,
    signInWithCustomToken,
} from 'firebase/auth';
import { auth } from 'firebaseui';
import { firebaseConfig } from 'lib/constants';
import {
    getDatabase,
    ref,
    get,
    set,
    push,
    onChildAdded,
    remove,
    onChildRemoved,
    DataSnapshot,
    update,
    query,
    orderByChild,
    startAt,
    connectDatabaseEmulator,
    onValue,
    off,
    onChildChanged,
} from 'firebase/database';
import { StoredUser } from 'store/features/authentication';
import {
    FirebaseUser,
    FireBaseNewPortalUsersTable,
    FirebaseUserProtected,
    UserSession,
    UserSessionKeys,
    UserSessionEntryTypes,
    ConsumerAppUser,
} from 'types/User/firebaseTypes';
import { Fillup, FirebaseTicket } from 'types/User/firebaseTypes/ticket';
import {
    MappedPhysicalDeviceInput,
    RawDeliveryType,
    RawShipmentsData,
    SupplierInfo,
    RawSupplierGroups,
    Device,
    RawSuppliersList,
    RawSupplierStats,
    SupplierInput,
    SupplierDefaultValues,
    DefaultUser,
    SupportedBackOffices,
    CarGasConnectionDetails,
    AddsConnectionDetails,
} from 'types';
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    deleteObject,
    getDownloadURL,
    connectStorageEmulator,
} from 'firebase/storage';

import _ from 'lodash';
import { IssueTicketOption } from 'types/Issue';
import { AlertsConfig } from 'views/DeviceDetails/views/DeviceAlertsConfig/DeviceAlertsConfig';
import { OpsPortalColumnSettings } from 'components/data/DataTable/opsMapping';
import { recursivelyNullifyUndefinedValues } from 'helpers/general';
import { ConsumerInfoData } from 'views/Consumers/ConsumerDetails/ConsumerInfo/ConsumerInfo';

export const firebaseApp = initializeApp(firebaseConfig);

export const googleAuthProvider = new GoogleAuthProvider();
export const emailAuthProvider = new EmailAuthProvider();
export const firebaseAuthObj =
    process.env.JEST_WORKER_ID === undefined ? getAuth() : null;
export const firebaseDatabase = getDatabase();
export const firebaseStorage = getStorage();

// if running locally, use the firebase emulators for auth, database, functions, and storage
if (process.env.REACT_APP_MODE === 'local') {
    const emuHost = process.env.REACT_APP_FIREBASE_EMU_HOST || '';
    const dbPort =
        parseInt(process.env.REACT_APP_FIREBASE_DATABASE_EMULATOR_PORT!) || 0;
    const storagePort =
        parseInt(process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT!) || 0;

    if (!!firebaseAuthObj)
        connectAuthEmulator(
            firebaseAuthObj,
            `http://${emuHost}:${process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT}`
        );
    connectDatabaseEmulator(firebaseDatabase, emuHost, dbPort);
    connectStorageEmulator(firebaseStorage, emuHost, storagePort);
}
export const firebaseUiInstance =
    process.env.JEST_WORKER_ID === undefined
        ? new auth.AuthUI(firebaseAuthObj)
        : null;

/** Users */

/** Get all of a supplier's consumer end-users (i.e., homeowner). */
export const getConsumerAppUsers = async (supplierId: string) => {
    const usersRef = ref(firebaseDatabase, `suppliers/${supplierId}/users`);
    const _supplierUsers = await get(usersRef);
    const supplierUsers = _supplierUsers.val();
    if (supplierUsers) {
        const userPromises = Object.keys(supplierUsers).map(
            async (userId: string) => {
                const userRef = ref(
                    firebaseDatabase,
                    `suppliers/${supplierId}/users/${userId}`
                );

                const confirm = await get(userRef);
                if (confirm.val()) {
                    return getUser(userId).then((userData) => {
                        if (
                            userData &&
                            userData.protected &&
                            !userData.protected.supplierId
                        ) {
                            const user: ConsumerAppUser = userData.info ?? {};

                            user.uid = confirm.key ?? '';
                            user.read_only =
                                userData.protected?.read_only ?? false;
                            user.lastLoginApp =
                                userData.info.lastLoginApp ?? -1;
                            user.lastLoginPortal =
                                userData.info.lastLoginPortal ?? -1;

                            if (userData.protected?.devices) {
                                user.deviceCount = Object.keys(
                                    userData.protected?.devices
                                ).length;
                            } else {
                                user.deviceCount = 0;
                            }

                            return user;
                        }
                    });
                }
            }
        );

        return (await Promise.all(userPromises)).filter((user) => !!user);
    }
    return supplierUsers;
};

/** Update a consumer end-user's (i.e., homeowner) information. */
export const updateConsumerUserInfo = async (
    consumerUserData: ConsumerInfoData
) => {
    if (consumerUserData.uid) {
        const userRef = ref(
            firebaseDatabase,
            `users/${consumerUserData.uid}/info`
        );
        return await update(userRef, {
            name: consumerUserData.name,
            phone: consumerUserData.phone,
            address: consumerUserData.address,
            email: consumerUserData.email,
        });
    }
};

/** Invite a consumer end-user (i.e., homeowner) to TU application. */
export const inviteConsumerAppUser = async (
    supplierId: string,
    deviceId: string,
    inviteEmail: string
) => {
    const supplierInfoRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info`
    );

    const supplierInfoData = await get(supplierInfoRef);
    const supplierInfo = supplierInfoData.val();

    const newAppUsersRef = ref(
        firebaseDatabase,
        `pending/inviteAppUsers/${supplierId}`
    );

    const payload = {
        device_id: deviceId,
        supplier_id: supplierId,
        supplier_name: supplierInfo.name,
        invite_email: inviteEmail,
    };

    push(newAppUsersRef, payload).then(() => {
        const newAppUserRef = ref(
            firebaseDatabase,
            `suppliers/${supplierId}/newAppUsers`
        );

        onChildChanged(newAppUserRef, (event) => {
            const eventData = event.val();

            if (eventData.email === inviteEmail && eventData.error) {
                const addedAppUserRef = ref(
                    firebaseDatabase,
                    `suppliers/${supplierId}/newAppUsers/${eventData.key}`
                );

                remove(addedAppUserRef).then(() => {
                    off(newAppUserRef);
                });
            } else if (eventData.email === inviteEmail) {
                off(newAppUserRef);
            }
        });
    });
};

export const updateUserTermsOfService = async (
    uid: string,
    hasAgreed: boolean
) => {
    const tosRef = ref(firebaseDatabase, `users/${uid}/info/tos`);
    if (hasAgreed) {
        await set(tosRef, true);
    }
};

export const getUserInfo = async (
    uid: string
): Promise<FirebaseUser['info']> => {
    const pathRef = ref(firebaseDatabase, `users/${uid}/info`);
    const info = await get(pathRef);
    return info.toJSON() as FirebaseUser['info'];
};

export const updateUserLastLoginPortal = async (
    uid: string,
    supplierId: string
) => {
    const newLoginDate = new Date().getTime();

    const pathRef = ref(firebaseDatabase, `users/${uid}/info/lastLoginPortal`);
    const supplierRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/lastLoginPortal`
    );
    await set(pathRef, newLoginDate);
    return await set(supplierRef, newLoginDate);
};

export const getPortalUsersStatic = async (supplierId: string) => {
    const usersRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/portalUsers`
    );

    return get(usersRef).then((result) => {
        if (!result.val || !result.val()) {
            return [];
        }

        const userPromises = Object.keys(result.val()).map(
            async (uid: string) => {
                const userRef = ref(firebaseDatabase, `users/${uid}/info`);
                const user = await get(userRef);

                return user.val();
            }
        );

        return Promise.all(userPromises).then((users) => {
            return users.filter((u) => u);
        });
    });
};

export const getNewPortalUsers = async (supplierId: string) => {
    const newPortalUsersRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/newPortalUsers`
    );
    const data = await get(newPortalUsersRef);
    return data.toJSON() as FireBaseNewPortalUsersTable;
};

/**
 * retrieve user information from firebase
 * @note In some instances, the user id may be unavailable/unassigned or may be set to the automated "Auto" or "Confirmed Auto" settings.
 * @param {string} uid - user id string
 * @returns user or N/A if unavailable
 */
export const getUser = async (uid?: string | null) => {
    if (uid && uid !== 'Auto' && uid !== 'Confirmed Auto') {
        const userRef = ref(firebaseDatabase, `users/${uid}`);
        try {
            const data = await get(userRef);
            return data.toJSON() as FirebaseUser;
        } catch (e) {
            return null;
        }
    }
    return null;
};

/**
 * retrieve protected user information from firebase
 * @see getUser for more information regarding the uid checks
 * @param {string} uid - user id string
 * @returns user or N/A if unavailable
 */
export const getUserProtected = async (uid?: string | null) => {
    if (uid && uid !== 'Auto' && uid !== 'Confirmed Auto') {
        const userProtectedRef = ref(
            firebaseDatabase,
            `users/${uid}/protected`
        );
        try {
            const data = await get(userProtectedRef);
            return data.toJSON() as FirebaseUserProtected;
        } catch (e) {
            return null;
        }
    }
    return null;
};

export const createAuthData = async (user: any): Promise<StoredUser> => {
    const idToken: string = await user.getIdToken();
    const storedUser: StoredUser = {
        displayName: user.displayName,
        email: user.email,
        emailVerified: user.emailVerified,
        photoURL: user.photoURL,
        refreshToken: user.refreshToken,
        uid: user.uid,
        idToken: idToken,
        protected: null,
        providerIds: [],
    };
    for (const provider of user.providerData)
        storedUser.providerIds?.push(provider.providerId);
    return storedUser;
};

export const updateUserPermissions = async (
    currentUserId: string,
    updatedUserId: string,
    readOnly: boolean,
    roleId: number
) => {
    const pathRef = ref(
        firebaseDatabase,
        `pending/admin/update_permissions/${currentUserId}/${updatedUserId}`
    );
    await set(pathRef, {
        read_only: readOnly,
        roleId: roleId,
    });
};

export const addPortalUser = async (supplierId: string, email: string) => {
    const pathRef = ref(
        firebaseDatabase,
        `pending/newPortalUsers/${supplierId}`
    );

    push(pathRef, {
        supplierId,
        invite_email: email,
    }).then(() => {
        const newUserRef = ref(
            firebaseDatabase,
            `suppliers/${supplierId}/newPortalUsers`
        );

        onChildAdded(newUserRef, (event) => {
            const eventData = event.val();

            if (eventData.email === email) {
                if (eventData.error) {
                    const addedUserRef = ref(
                        firebaseDatabase,
                        `suppliers/${supplierId}/newPortalUsers/${eventData.key}`
                    );
                    remove(addedUserRef).then(() => {
                        // kill the listener
                        off(newUserRef);
                    });
                } else {
                    // kill the listener
                    off(newUserRef);
                }
            }
        });
    });
};

export const removePortalUser = async (
    currentUserId: string,
    removedUserId: string
) => {
    const removalRef = ref(
        firebaseDatabase,
        `pending/admin/remove_portal_user/${currentUserId}/${removedUserId}`
    );
    const listenRef = ref(
        firebaseDatabase,
        `pending/admin/remove_portal_user/${currentUserId}`
    );
    const removeChild = () => {
        return new Promise<void>((resolve) => {
            set(removalRef, true).then(() => {
                const removeCallback = onChildRemoved(listenRef, (_event) => {
                    removeCallback();
                    resolve();
                });
            });
        });
    };
    await removeChild();
};

/** Get info for new users so they can set passwords */
export const getInvites = async (inviteCode: string) => {
    const inviteRef = ref(firebaseDatabase, 'invites/' + inviteCode);

    const getInvitedUser = () => {
        return new Promise<StoredUser>((resolve, reject) => {
            const removeCallback = onValue(inviteRef, async (res) => {
                const inviteObject = res.val();

                try {
                    if (inviteObject.token && !!firebaseAuthObj) {
                        signInWithCustomToken(
                            firebaseAuthObj,
                            inviteObject.token
                        )
                            .then(async (authData) => {
                                const userInfoRef = ref(
                                    firebaseDatabase,
                                    `users/${authData.user.uid}`
                                );
                                const user = await get(userInfoRef);

                                if (user.val()) {
                                    removeCallback();
                                    resolve(user.val()?.info);
                                } else {
                                    removeCallback();
                                    reject();
                                }
                            })
                            .catch((error) => {
                                removeCallback();
                                reject(error);
                            });
                    } else {
                        const pendingInviteRef = ref(
                            firebaseDatabase,
                            'pending/invites/' + inviteCode
                        );
                        if (inviteObject.uid) {
                            return set(pendingInviteRef, inviteObject.uid);
                        }
                    }
                } catch (e) {
                    return reject(e);
                }
            });
        });
    };

    return await getInvitedUser();
};

/** Get list of end-users associated with the device (i.e., who own the particular device). */
export const getDeviceUsers = async (deviceId: string, supplierId: string) => {
    if (!deviceId) {
        throw new Error('deviceId is required.');
    }
    const deviceRef = ref(firebaseDatabase, `devices/${deviceId}`);
    const device = await get(deviceRef);

    if (!device || !device.val) {
        return [];
    }

    const deviceUsers = device.val()?.protected?.users;

    if (deviceUsers) {
        const userPromises = Object.keys(deviceUsers).map(async (userId) => {
            const userRef = ref(
                firebaseDatabase,
                `suppliers/${supplierId}/users/${userId}`
            );
            const confirm = await get(userRef);
            if (confirm.val()) {
                return getUser(userId).then((result) => {
                    if (result) {
                        return result;
                    }
                });
            }
        });

        return (await Promise.all(userPromises)).filter(
            (user): user is FirebaseUser => !!user
        );
    }
};

/** Get the user session data for the specified user uid. */
export const getUserSession = async (uid: string): Promise<UserSession> => {
    const userSessionRef = ref(firebaseDatabase, `users/${uid}/session`);

    const userSession = await get(userSessionRef).then((res) => res.val());

    return userSession;
};

/**
 * Update the session for the user that contains many settings for various parts of the application
 * @param uid - user id string
 * @param sessionSectionId - section of the app that we are changing
 * @param newSession - new session details of the section of the app we are changing
 */
export const updateUserSession = async (
    uid: string,
    sessionSectionId: UserSessionKeys,
    newSession: OpsPortalColumnSettings | UserSessionEntryTypes
) => {
    const userSessionRef = ref(firebaseDatabase, `users/${uid}/session`);

    const currentUserSession = await get(userSessionRef).then((res) =>
        res.val()
    );

    let mappedSession;

    // Update an existing session's existing session entry.
    if (currentUserSession && sessionSectionId in currentUserSession) {
        mappedSession = {
            ...currentUserSession,
            [sessionSectionId]: {
                ...currentUserSession[sessionSectionId],
                ...newSession,
            },
        };
    }
    // Update an existing session with a new entry.
    else if (currentUserSession) {
        mappedSession = {
            ...currentUserSession,
            [sessionSectionId]: {
                ...newSession,
            },
        };
    }
    // Create a session when no previous session data at all exists for the user.
    else {
        mappedSession = {
            [sessionSectionId]: {
                ...newSession,
            },
        };
    }

    return await set(userSessionRef, mappedSession);
};

/**
 * Update the group component of the user session.
 * @param {string} uid - user id string
 * @param {string[]} newGroups - list of group names that are currently active filters
 */
export const updateUserGroupSession = async (
    uid: string,
    newGroups: string[]
) => {
    const userSessionRef = ref(firebaseDatabase, `users/${uid}/session`);
    const updates = {
        groups: newGroups,
    };
    return await update(userSessionRef, updates);
};

/** upload supplier icon to fb */
export const uploadSupplierIcon = async (supplierId: string, file: File) => {
    const supplierIconRef = storageRef(
        firebaseStorage,
        `icons/supplier_logo_${supplierId}.png`
    );

    return await uploadBytes(supplierIconRef, file);
};

/** get supplier icon to fb */
export const getSupplierIcon = async (supplierId: string) => {
    const supplierIconRef = storageRef(
        firebaseStorage,
        `icons/supplier_logo_${supplierId}.png`
    );

    return await getDownloadURL(supplierIconRef);
};

/** remove supplier icon to fb */
export const removeSupplierIcon = async (supplierId: string) => {
    const supplierIconRef = storageRef(
        firebaseStorage,
        `icons/supplier_logo_${supplierId}.png`
    );

    return await deleteObject(supplierIconRef);
};

/** Tickets (deliveries) */

export const getTickets = async (supplierId: string) => {
    const ticketsRef = ref(firebaseDatabase, `ticket/${supplierId}/active`);
    const data = await get(ticketsRef);
    const ticketList: Array<FirebaseTicket> = [];
    const ticketMapper = function (rawTicket: DataSnapshot) {
        const ticket = rawTicket.val();
        ticket.id = rawTicket.key;
        return ticket;
    };

    data.forEach((rawDevice) => {
        rawDevice.forEach((rawTicket) => {
            ticketList.push(ticketMapper(rawTicket));
        });
    });
    return ticketList;
};

export const getLastFillup = async (deviceId: string) => {
    const fillupRef = ref(firebaseDatabase, `/fillups/${deviceId}/`);
    const data = await get(fillupRef);
    const fillupList: Fillup[] = [];

    data.forEach((rawFillup) => {
        fillupList.push({ ...rawFillup.val(), id: rawFillup.key });
    });

    fillupList.sort((a, b) => {
        const timeA = new Date(a.time).valueOf();
        const timeB = new Date(b.time).valueOf();

        return timeA - timeB;
    });

    return fillupList[0];
};

export const createTicket = async (
    supplierId: string,
    deviceId: string,
    ticket: Partial<FirebaseTicket>
) => {
    const deviceRef = ref(
        firebaseDatabase,
        `ticket/${supplierId}/active/${deviceId}`
    );
    const result = await push(deviceRef, ticket);
    const ticketRef = ref(
        firebaseDatabase,
        `ticket/${supplierId}/active/${deviceId}/${result.key}`
    );
    await update(ticketRef, { id: result.key });
    return Promise.resolve({ id: result.key, ...ticket });
};

/** Shipments */

export const getRawShipments = async (supplierId: string) => {
    const shipmentsRef = ref(firebaseDatabase, `sf_shipments/${supplierId}`);
    const data = await get(shipmentsRef);
    return data.toJSON() as RawShipmentsData;
};

/** Device */

export const getDeviceAlertFirebase = async (
    deviceId: string,
    supplierId: string
) => {
    const alertRef = ref(firebaseDatabase, `alerts/${deviceId}/${supplierId}`);
    const alert = await get(alertRef);

    return alert.val();
};

export const updateDeviceAlert = async (
    deviceId: string,
    supplierId: string,
    alert: Partial<AlertsConfig>
) => {
    const alertRef = ref(firebaseDatabase, `alerts/${deviceId}/${supplierId}`);

    const result = await update(alertRef, alert);

    return result;
};

/** Suppliers */

/** Get the SupplierInfo data for the specified supplierId. */
export const getSupplierInfo = async (supplierId: string) => {
    const supplierInfoRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info`
    );
    const data = await get(supplierInfoRef);
    return data.toJSON() as SupplierInfo;
};

/** Update supplier info in firebase. */
export const updateSupplierInfo = async (
    supplierId: string,
    info: Partial<SupplierInfo>
) => {
    const supplierInfoRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info`
    );
    await update(supplierInfoRef, info);
};

/** Get the default values for the supplier account. */
export const getSupplierDefaultValues = async (
    supplierId: string
): Promise<SupplierDefaultValues> => {
    const supplierRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/defaults/values`
    );
    const data = await get(supplierRef);
    return data.toJSON() as SupplierDefaultValues;
};

/** Update supplier default values. */
export const updateSupplierDefaultValues = async (
    supplierId: string,
    updatedDefaults: SupplierDefaultValues
): Promise<void> => {
    // If optional fields were previously filled and then deleted by the user, we need to set that undefined field to null to have the data update properly.
    updatedDefaults = recursivelyNullifyUndefinedValues(updatedDefaults);
    const supplierRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/defaults/values`
    );
    await update(supplierRef, updatedDefaults);
};

/** Add new supplier */
export const addSupplier = async (newSupplier: SupplierInput) => {
    const supplierRef = ref(firebaseDatabase, 'pending/newSupplier');
    return await push(supplierRef, newSupplier);
};

/** Get all delivery types associated with the supplier's account. */
export const getSupplierDeliveryTypes = async (
    supplierId: string
): Promise<RawDeliveryType> => {
    const supplierRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/delivery_types`
    );
    const data = await get(supplierRef);
    return data.toJSON() as RawDeliveryType;
};

/**
 * Make network call to firebase to remove a delivery type from a supplier.
 * @param {string} supplierId Supplier ID to remove the delivery type from.
 * @param {string} deliveryTypeName The name of the delivery type to remove.
 */
export const removeSupplierDeliveryType = async (
    supplierId: string,
    deliveryTypeName: string
): Promise<void> => {
    const deliveryTypeRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/delivery_types`
    );

    const payload: any = {};
    payload[deliveryTypeName] = null;

    await update(deliveryTypeRef, payload);
};

/**
 * Make network call to firebase to add/update a delivery type from a supplier.
 * @param {string} supplierId Supplier ID to add/update the delivery type to.
 * @param {string} deliveryTypeName The name of the delivery type to add/update.
 * @param {string} willCall Value of the willCall param of a delivery type.
 */
export const addOrUpdateSupplierDeliveryType = async (
    supplierId: string,
    deliveryTypeName: string,
    willCall: boolean
): Promise<void> => {
    const deliveryTypeRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/delivery_types`
    );

    const payload: any = {};
    payload[deliveryTypeName] = willCall;

    await update(deliveryTypeRef, payload);
};

/** Get all groups associated with the supplier's account. */
export const getSupplierGroups = async (
    supplierId: string
): Promise<RawSupplierGroups> => {
    const supplierRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/groups`
    );
    const data = await get(supplierRef);
    return data.toJSON() as RawSupplierGroups;
};

/**
 * Make network call to firebase to remove a group from a supplier.
 * @param {string} supplierId Supplier ID to remove the group from.
 * @param {string} groupName The name of the group to remove.
 */
export const removeSupplierGroup = async (
    supplierId: string,
    groupName: string
): Promise<void> => {
    const groupRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/groups/${groupName}`
    );
    await remove(groupRef);
};

/**
 * Make network call to firebase to add a new group to a supplier.
 * @param {string} supplierId Supplier ID to add the group to.
 * @param {string} groupName The name of the group to add.
 * @param {string} description The description of the group to add.
 */
export const addSupplierGroup = async (
    supplierId: string,
    groupName: string,
    description: string
): Promise<void> => {
    const groupRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/info/groups/${groupName}`
    );

    await set(groupRef, description);
};

/** Get supplier info. */
export const getSupplierInfoFirebase = async (
    supplierId: string
): Promise<SupplierInfo> => {
    const supplierRef = ref(firebaseDatabase, `suppliers/${supplierId}/info`);
    const data = await get(supplierRef);
    return data.toJSON() as SupplierInfo;
};

/** Admin only. Get all suppliers. Raw result is NOT an array but rather an object mapping supplierIds to supplier names.*/
export const getAllRawSuppliers = async (): Promise<RawSuppliersList> => {
    const supplierRef = ref(firebaseDatabase, 'supplierList/');
    const data = await get(supplierRef);
    return data.toJSON() as RawSuppliersList;
};

/** Get supplier stats for specified supplierId. */
export const getRawSupplierStats = async (
    supplierId: string
): Promise<RawSupplierStats> => {
    const supplierStatsRef = ref(firebaseDatabase, `conn-stats/${supplierId}`);
    const data = await get(supplierStatsRef);
    return data.toJSON() as RawSupplierStats;
};

/** Get supplier default users */
export const getFirebaseDefaultUsers = async (
    supplierId: string
): Promise<DefaultUser[]> => {
    const defaultUserRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/defaults/users`
    );
    const data = await get(defaultUserRef);
    return data.toJSON() as DefaultUser[];
};

/** Update supplier's default users. */
export const updateFirebaseDefaultUsers = async (
    supplierId: string,
    user: DefaultUser
): Promise<void> => {
    const defaultUserValues: Omit<DefaultUser, 'uid' | 'email'> = {
        threshold: user.threshold || false,
        offline: user.offline || false,
        deploy: user.deploy || false,
        issue: user.issue || false,
    };

    const defaultUserRef = ref(
        firebaseDatabase,
        `suppliers/${supplierId}/defaults/users/${user.uid}`
    );

    await update(defaultUserRef, defaultUserValues);
};

/** Get supplier's third-party backoffice data (use getCargasBackoffice for cargas and getAddsBackoffice for adds though as those have specialized return types.) from firebase. */
export const getFirebaseSupplierBackoffice = async (
    supplierId: string,
    backofficeName: SupportedBackOffices
): Promise<any> => {
    const supplierBackofficeRef = ref(
        firebaseDatabase,
        `backoffice/${backofficeName}/suppliers/${supplierId}`
    );
    const data = await get(supplierBackofficeRef);
    return data.toJSON();
};

/** Get backoffice configuration for CarGas third-party backoffice from firebase. */
export const getFirebaseCargasBackoffice = async (
    supplierId: string
): Promise<CarGasConnectionDetails> => {
    const supplierBackofficeRef = ref(
        firebaseDatabase,
        `backoffice/cargas/suppliers/${supplierId}`
    );
    const data = await get(supplierBackofficeRef);
    return data.toJSON() as CarGasConnectionDetails;
};

/** Get backoffice configuration for ADDs third-party backoffice from firebase. */
export const getFirebaseAddsBackoffice = async (
    supplierId: string
): Promise<AddsConnectionDetails> => {
    const supplierBackofficeRef = ref(
        firebaseDatabase,
        `backoffice/adds/suppliers/${supplierId}`
    );
    const data = await get(supplierBackofficeRef);
    return data.toJSON() as AddsConnectionDetails;
};

/** Delete a the previous backoffice entry for the supplier in firebase. */
export const removeFirebaseBackoffice = async (
    supplierId: string,
    oldBackoffice: SupportedBackOffices
): Promise<void> => {
    const supplierBackofficeRef = ref(
        firebaseDatabase,
        `backoffice/${oldBackoffice}/suppliers/${supplierId}`
    );
    await remove(supplierBackofficeRef);
};

/** Update supplier third-party backoffice information in firebase. */
export const updateFirebaseBackoffice = async (
    supplierId: string,
    backofficeName: SupportedBackOffices,
    backofficeData: any,
    ftpUsername: string
): Promise<void> => {
    const supplierBackofficeRef = ref(
        firebaseDatabase,
        `backoffice/${backofficeName}/suppliers/${supplierId}`
    );

    if (['cargas', 'adds'].includes(backofficeName)) {
        await set(supplierBackofficeRef, backofficeData);
    } else {
        await set(supplierBackofficeRef, ftpUsername).then(() => {
            if (backofficeName !== 'cargas' && backofficeName !== 'adds') {
                const ftpRef = ref(
                    firebaseDatabase,
                    `pending/ftpUser/mod/${supplierId}`
                );
                set(ftpRef, {
                    username: ftpUsername,
                    backoffice: backofficeName,
                });
            }
        });
    }
};

/** Get all supplier rankings */
export const getRanks = async () => {
    const ranksRef = ref(firebaseDatabase, 'supplierRank');

    return await (await get(ranksRef)).val();
};

/** Readings */

export const watchForDeviceReading = async (deviceId: string) => {
    const readingsRef = query(
        ref(firebaseDatabase, `readings/${deviceId}`),
        orderByChild('insert_time'),
        startAt(Date.now())
    );

    const getReading = () => {
        return new Promise<any>((resolve) => {
            const removeCallback = onChildAdded(readingsRef, (event) => {
                const eventData = event.val();
                removeCallback();
                resolve(eventData);
            });
        });
    };
    return await getReading();
};

/** Devices */

export const updateCreatePhysicalDeviceSession = async (
    supplierId: string,
    deviceId: string,
    session: any
) => {
    const sessionIdRef = ref(
        firebaseDatabase,
        `deployments/${supplierId}/${deviceId}`
    );

    const sessionId = await push(sessionIdRef).then((val) => val.key);

    const deploymentRef = ref(
        firebaseDatabase,
        `deployments/${supplierId}/${deviceId}/${sessionId}`
    );

    return await update(deploymentRef, session);
};

export const savePhysicalDevice = async (
    deviceInput: MappedPhysicalDeviceInput
) => {
    const device = _.omitBy(
        deviceInput,
        (v, k) =>
            k.charAt(0) === '$' ||
            k.charAt(0) === '_' ||
            typeof v === 'function'
    );

    if (!deviceInput.deviceId) {
        throw new Error('deviceId is required.');
    }

    const deviceRef = ref(firebaseDatabase, `devices/${deviceInput.deviceId}`);

    return await update(deviceRef, device);
};

/** Tickets */

export const getDeviceTickets = async (
    supplierId: string,
    deviceId: string
) => {
    const deviceTicketsRef = ref(
        firebaseDatabase,
        'ticket/' + supplierId + '/active/' + deviceId
    );

    const data = await get(deviceTicketsRef).then((d) => {
        const val = d.val();
        return val;
    });

    return data;
};

export const getDeviceDeploymentSessions = async (
    deviceId: string,
    supplierId: string
) => {
    const deployRef = ref(
        firebaseDatabase,
        `deployments/${supplierId}/${deviceId}`
    );

    return await (await get(deployRef)).val();
};

export const getDeviceAudit = async (deviceId: string) => {
    const auditRef = ref(firebaseDatabase, `audit/devices/${deviceId}`);

    return await (await get(auditRef)).val();
};

/** Tickets (issues, etc) */

export const submitDeviceHelp = async (
    user: StoredUser,
    device: Device,
    selectedIssue: IssueTicketOption,
    description: string,
    offlineLedSequence?: string
) => {
    const payload = {
        userSupplierId: user.protected?.supplierId,
        email: user.email,
        action: selectedIssue.action,
        issue: selectedIssue.text,
        issue_description: description,
        support_doc_url: selectedIssue.support_doc_url ?? '',
        deviceSupplierId: device.protected?.supplierId ?? '',
        deviceId: device.deviceId,
        shortDeviceId: device.shortDeviceId,
        deviceName: device.name ?? '',
        deviceAddress: device.address ?? '',
        deviceAccountId: device.accountId ?? '',
        offlineLedSequence,
    };

    const deviceHelpRef = ref(
        firebaseDatabase,
        `pending/deviceHelp/${user.uid}`
    );

    const result = await push(deviceHelpRef, payload);

    return result;
};

/** Audit */
/** Update the audit log firebase entry at the audit/${target} firebase table. */
export const updateFirebaseAuditLog = async (
    target: string,
    payload: any
): Promise<void> => {
    const auditRef = ref(firebaseDatabase, `audit/${target}`);

    await push(auditRef, payload);
};
